import React from 'react';
import { Column } from '../../hooks/useTablePages';
import { format, parseISO } from 'date-fns';
import StatusDot, { StatusStyle } from '../../components/Status/StatusDot';
import { AssemblyItem } from './Assemblies.query';
import { Flex, Text } from 'rebass';
import {
  assemblyCompliantStyle,
  assemblyTestStatusStyle,
} from '../../components/BackflowAssembly/assemblyTestStatus';
import { constantToLabel } from '@ecosconnect/core/src/helpers/string';
import colors from '../../theme/colors';
import StatusChip from '../../components/Status/StatusChip';

const columns: Column[] = [
  // {
  //   header: 'id',
  //   accessor: 'id',
  //   width: 80,
  // },
  {
    header: 'Make',
    accessor: 'model.backflowAssemblyMake.name',
    width: 140,
  },
  {
    header: 'Model',
    accessor: 'model.name',
    width: 110,
  },
  {
    header: 'Serial',
    accessor: 'serialNumber',
    width: 120,
  },
  {
    header: 'Type',
    id: 'type',
    accessor: (row: AssemblyItem) => {
      return (row.type || '').replace(/_/g, ' ');
    },
    width: 100,
  },
  {
    header: 'Size',
    id: 'size',
    accessor: (row: AssemblyItem): string => (row.size ? row.size + '"' : ''),
    width: 80,
  },
  {
    header: 'Test Status',
    id: 'testStatus',
    accessor: (row: AssemblyItem) => {
      const style: StatusStyle = assemblyTestStatusStyle(row.testStatus);
      return (
        <Flex flexDirection="row" sx={{ gap: 1 }}>
          <StatusDot status={style} />
          <Text variant="body2">{constantToLabel(row.testStatus)}</Text>
        </Flex>
      );
    },
    stringAccessor: (row: AssemblyItem): string => {
      return constantToLabel(row.testStatus);
    },
    width: 140,
  },
  {
    header: 'Tags',
    id: 'tags.text',
    accessor: (row: AssemblyItem) => {
      return (
        <>
          {(row.tags ?? []).map((tag: string) => (
            <StatusChip status="default" key={tag} mr="1">
              {tag}
            </StatusChip>
          ))}
        </>
      );
    },
    stringAccessor: (row: AssemblyItem): string => {
      return (row.tags ?? []).join(', ');
    },
    width: 150,
  },
  {
    header: 'Scheduled Company',
    accessor: 'scheduledCompany',
    width: 180,
  },
  {
    header: 'Compliant',
    id: 'compliantReason',
    accessor: (row: AssemblyItem) => {
      const style: StatusStyle = assemblyCompliantStyle(row.compliant);
      return (
        <Flex flexDirection="row" sx={{ gap: 1 }}>
          <StatusDot status={style} />
          <Text variant="body2">{constantToLabel(row.compliantReason)}</Text>
        </Flex>
      );
    },
    stringAccessor: (row: AssemblyItem): string => {
      return constantToLabel(row.compliantReason);
    },
    width: 140,
  },
  {
    header: 'Test Anniversary',
    id: 'testAnniversary',
    accessor: (row: AssemblyItem) => {
      if (!row.testAnniversary || row.testAnniversary.startsWith('00')) {
        return '';
      }
      return format(parseISO(row.testAnniversary), 'MMM do');
    },
    width: 150,
  },
  {
    header: 'Last Tested',
    id: 'lastTestDate',
    accessor: (row: AssemblyItem) => {
      if (!row.lastTestDate || row.lastTestDate.startsWith('00')) {
        return '';
      }
      return format(parseISO(row.lastTestDate), 'M/d/yyyy');
    },
    width: 120,
  },
  {
    header: 'Last Result',
    id: 'lastTest.finalResult',
    accessor: (row: AssemblyItem) => {
      const { lastTest } = row;
      if (!lastTest?.finalResult || lastTest.finalResult === 'FAIL') {
        return <StatusDot status="error" />;
      }

      if (lastTest.initialResult === 'FAIL') {
        return <StatusDot color={colors.failPass} />;
      }

      return <StatusDot status="success" />;
    },
    stringAccessor: (row: AssemblyItem): string => {
      return constantToLabel(row.lastTest?.finalResult);
    },
    width: 95,
  },
  {
    header: 'Next Test Due',
    id: 'nextTestDate',
    accessor: (row: AssemblyItem) => {
      if (!row.nextTestDate || row.nextTestDate.startsWith('00')) {
        return '';
      }
      return format(parseISO(row.nextTestDate), 'M/d/yyyy');
    },
    width: 110,
  },
  {
    header: 'Install Date',
    id: 'installDate',
    accessor: (row: AssemblyItem) => {
      if (!row.installDate || row.installDate.startsWith('00')) {
        return '';
      }
      return format(parseISO(row.installDate), 'M/d/yyyy');
    },
    width: 110,
  },
  {
    header: 'Customer',
    id: 'customerName',
    accessor: (row: AssemblyItem) => row.customerName ?? '',
    width: 200,
  },
  {
    header: 'Property Type',
    id: 'waterService.type',
    accessor: (row: AssemblyItem) => {
      return constantToLabel(row.waterService?.type);
    },
    width: 130,
  },
  {
    header: 'Service',
    accessor: 'waterService.siteNumber',
    width: 110,
  },
  {
    header: 'Meter',
    accessor: 'waterService.meterNumber',
    width: 110,
  },
  {
    header: 'TRAC',
    accessor: 'waterService.pin',
    width: 110,
  },
  {
    header: 'Protection Type',
    id: 'protectionType',
    accessor: (row: AssemblyItem) => {
      return constantToLabel(row.protectionType);
    },
    width: 155,
  },
  {
    header: 'Hazard Type',
    accessor: 'hazardType',
    width: 155,
  },
  {
    header: 'Hazard Level',
    accessor: 'hazardLevel',
    width: 115,
  },
  {
    header: 'Address',
    id: 'address.street1',
    accessor: (row: AssemblyItem) =>
      row.address
        ? row.address.street1 +
          (row.address.street2 ? ', ' + row.address.street2 : '')
        : '',
    width: 220,
  },
  {
    header: 'City',
    accessor: 'address.city',
    width: 120,
  },
  {
    header: 'State',
    accessor: 'address.state',
    width: 80,
  },
  {
    header: 'Zip',
    id: 'address.zip',
    accessor: (row: AssemblyItem) =>
      row.address
        ? row.address.zip + (row.address.zipExt ? '-' + row.address.zipExt : '')
        : '',
    width: 120,
  },
  {
    header: 'Location',
    accessor: 'location',
    width: 300,
  },
];

const excluded = [
  'lastTest.finalResult',
  'waterService.type',
  'waterService.siteNumber',
  'scheduledCompany',
];
export const columnsCompany = columns.filter(
  (column) =>
    !excluded.includes(column.id || '') &&
    (typeof column.accessor !== 'string' || !excluded.includes(column.accessor))
);
const sizeIndex = columnsCompany.findIndex((column) => column.id === 'size');
columnsCompany.splice(sizeIndex, 0, {
  header: 'Water Supplier',
  accessor: 'waterSupplier.profile.name',
  width: 190,
});

export default columns;
